import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  generateProgressionsFromSteps,
  rhythmBeats,
  PROGRESSION_CATALOG,
  type HarmonicRhythm,
} from '@/music/progressions';
import { useStore } from '@/state/store';
import { voicingIdentityLabel } from '@/music/voicing';
import { PlayableDiagram } from '@/components/PlayableDiagram';
import { ReferencePage } from '@/components/ReferencePage';
import { getChordPlayer } from '@/audio/player';
import { useSequencePlaying } from '@/audio/useSequencePlaying';
import { Play, Repeat2, Square } from 'lucide-react';

export function ProgressionsPage() {
  const stringSet = useStore((s) => s.stringSet);
  const voicingFamily = useStore((s) => s.voicingFamily);
  const freeStringSet = useStore((s) => s.freeStringSet);
  const avoidB9 = useStore((s) => s.avoidB9);
  const audioEnabled = useStore((s) => s.audioEnabled);
  const tempo = useStore((s) => s.tempo);
  const [defId, setDefId] = useState(PROGRESSION_CATALOG[0].id);
  const [rhythm, setRhythm] = useState<HarmonicRhythm>('long');
  // Which pattern is armed to loop (mutually exclusive), or null for none.
  const [loopIndex, setLoopIndex] = useState<number | null>(null);
  const sequencePlaying = useSequencePlaying();
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);

  const def = useMemo(
    () => PROGRESSION_CATALOG.find((d) => d.id === defId) ?? PROGRESSION_CATALOG[0],
    [defId],
  );

  useEffect(() => {
    if (!sequencePlaying) setPlayingIndex(null);
  }, [sequencePlaying]);

  // Changing the idiom or its rhythm re-voices the diagrams; stop any running
  // loop so playback never lags behind what's on screen.
  useEffect(() => {
    getChordPlayer().stop();
  }, [defId, rhythm, voicingFamily]);

  // A different progression is a fresh context — clear any armed loop.
  useEffect(() => {
    setLoopIndex(null);
  }, [defId]);

  const patterns = useMemo(
    () =>
      generateProgressionsFromSteps(def.steps, stringSet, {
        avoidB9,
        freeStringSet,
        voicingFamily,
      }),
    [def, stringSet, avoidB9, freeStringSet, voicingFamily],
  );

  const beats = useMemo(() => rhythmBeats(def, rhythm), [def, rhythm]);

  const play = (index: number, doLoop: boolean) => {
    const pattern = patterns[index];
    if (!pattern || !audioEnabled) return;
    const secPerBeat = 60 / tempo;
    getChordPlayer().playSequence(
      pattern.chords.map((c) => ({ fingering: c.voicing, stringSet: c.stringSet })),
      { gaps: beats.map((b) => b * secPerBeat), loop: doLoop },
    );
    setPlayingIndex(index);
  };

  // Arm loop for one pattern; clicking the armed one turns it off. Only one can
  // be armed at a time. Toggling the pattern that is playing takes effect live.
  const toggleLoop = (index: number) => {
    const next = loopIndex === index ? null : index;
    setLoopIndex(next);
    if (sequencePlaying && playingIndex === index) {
      if (next === index) play(index, true);
      else getChordPlayer().stop();
    }
  };

  return (
    <ReferencePage
      className="progressions-page"
      title="Progressions"
      controls={
        <>
          <div className="control-group">
            <span className="label">Progression</span>
            <select
              value={defId}
              onChange={(e) => setDefId(e.target.value)}
              aria-label="Progression"
              data-testid="progression-select"
            >
              {PROGRESSION_CATALOG.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <span className="label">Harmonic rhythm</span>
            <div className="segmented" role="group" aria-label="Harmonic rhythm">
              <button
                type="button"
                className={rhythm === 'long' ? 'active' : ''}
                aria-pressed={rhythm === 'long'}
                onClick={() => setRhythm('long')}
              >
                Long
              </button>
              <button
                type="button"
                className={rhythm === 'short' ? 'active' : ''}
                aria-pressed={rhythm === 'short'}
                onClick={() => setRhythm('short')}
              >
                Short
              </button>
            </div>
          </div>
        </>
      }
    >
      {patterns.length === 0 && (
        <p className="empty-hint">No voicings found with the current interval filter.</p>
      )}

      {patterns.map((pattern, index) => (
        <div className="progression" key={index}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h3>
              {def.name} — Pattern {index + 1}
              <span className="muted" style={{ fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
                {def.degrees} · movement {pattern.totalDistance}
                {/* Only label the sets when the pattern actually mixes them. */}
                {new Set(pattern.chords.map((c) => c.stringSet)).size > 1
                  ? ` · ${pattern.stringSetPattern}`
                  : ''}
              </span>
            </h3>
            {audioEnabled && (
              <div className="progression-transport">
                {sequencePlaying && playingIndex === index ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-stop"
                    aria-label={`Stop pattern ${index + 1}`}
                    onClick={() => getChordPlayer().stop()}
                  >
                    <Square size={13} fill="currentColor" aria-hidden="true" />
                    Stop
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-sm"
                    aria-label={`Play pattern ${index + 1}`}
                    onClick={() => play(index, loopIndex === index)}
                  >
                    <Play size={14} fill="currentColor" aria-hidden="true" />
                    Play
                  </button>
                )}
                <button
                  type="button"
                  className={`btn btn-sm loop-toggle${loopIndex === index ? ' on' : ''}`}
                  aria-label="Loop this pattern"
                  aria-pressed={loopIndex === index}
                  title={loopIndex === index ? 'Stop looping this pattern' : 'Loop this pattern'}
                  data-testid={`progression-loop-${index}`}
                  onClick={() => toggleLoop(index)}
                >
                  <Repeat2 size={15} aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
          <div className="progression-chords">
            {pattern.chords.map((chord, chordIndex) => (
              <Fragment key={chordIndex}>
                <PlayableDiagram
                  chord={{
                    fingering: chord.voicing,
                    rootDisplay: chord.root,
                    chordType: chord.type,
                    symbol: chord.symbol,
                    inversion: chord.inversion,
                    stringSet: chord.stringSet,
                  }}
                  caption={
                    <>
                      {/* The symbol has to ride in the caption here. On the
                          Library and Scale Harmony a surrounding header names
                          the chord; a progression card has no such structure,
                          and the identity alone ("2nd inversion · ♭7 on top")
                          does not say WHICH chord you are looking at. */}
                      <span className="card-chord">{chord.symbol}</span>
                      {voicingIdentityLabel(chord.voicing, chord.root, chord.type)}
                    </>
                  }
                  showTitle={false}
                  highlightAvoid={avoidB9}
                />
                {chordIndex < pattern.chords.length - 1 && (
                  <div className="progression-arrow">→</div>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      ))}
    </ReferencePage>
  );
}
